import { addToWatchlist, removeFromWatchlist, setAnalysisExclusion } from '../store/slices/portfolioSlice';
import { settleChatAction } from '../store/slices/chatSlice';
import { debateLivePath, startDebate } from './debates';
import { tradingApi } from './trading';

// Executes an action Gladys proposed, once the user confirmed it, with the
// regular endpoints (so the rest of the app sees the change), then records
// the outcome on the message.

const orderCount = (action) => (action.targets || []).length;

const DONE_TOASTS = {
  watchlist_add: (s) => `${s} ajouté à la watchlist`,
  watchlist_remove: (s) => `${s} retiré de la watchlist`,
  analysis_exclude: (s) => `${s} exclu des rapports quotidiens`,
  analysis_include: (s) => `${s} réintégré aux rapports quotidiens`,
  debate_start: (s) => `Débat lancé sur ${s}`,
  order_ticket: (s) => `Ticket pré-rempli pour ${s} : vérifiez-le avant l’envoi`,
  trading_pause: () => 'Trading en pause : aucun ordre ne part',
  order_modify: (_, a) => {
    if (a.change?.order_type !== 'market') return 'Ordre modifié';
    return orderCount(a) > 1 ? `${orderCount(a)} ordres passés au marché` : 'Ordre passé au marché';
  },
  order_cancel: (_, a) => (orderCount(a) > 1 ? `${orderCount(a)} ordres annulés` : 'Ordre annulé'),
};

// orderTicketPath opens the trading ticket pre-filled with Gladys's order:
// nothing is sent before the user checks and confirms it there.
export const orderTicketPath = (action) => {
  const o = action.order || {};
  const q = new URLSearchParams({ symbol: action.symbol, side: o.side || 'buy', amount: String(o.amount || ''), type: o.order_type || 'limit', origin: 'gladys' });
  if (o.price) q.set('price', String(o.price));
  if (action.description) q.set('description', action.description);
  if (action.reason) q.set('reason', action.reason);
  return `/trading/ordre?${q.toString()}`;
};

export const actionDoneToast = (action) => (DONE_TOASTS[action.type] || ((s) => s))(action.symbol, action);

const errorText = (err) => (typeof err === 'string' ? err : err?.response?.data?.error || err?.message || 'L’action a échoué.');

// eachOrder acts on the action's orders one after the other (Saxo paces
// order requests): a failure does not stop the others, and the error names
// the orders that failed.
const eachOrder = async (action, run) => {
  const targets = action.targets || [];
  const failed = [];
  for (const target of targets) {
    try {
      await run(target);
    } catch (err) {
      failed.push(`${target.symbol || target.summary} : ${errorText(err)}`);
    }
  }
  if (failed.length) {
    const done = targets.length - failed.length;
    const s = done > 1 ? 's' : '';
    throw new Error(`${failed.join(' ; ')}${done ? ` (${done} autre${s} ordre${s} traité${s})` : ''}`);
  }
};

// options.confirmLive: the user ticked the real-money confirmation of an
// order change (the backend refuses a live change without it).
const execute = (dispatch, action, { confirmLive = false } = {}) => {
  switch (action.type) {
    case 'watchlist_add':
      return dispatch(addToWatchlist({ symbol: action.symbol, description: action.description || '' })).unwrap();
    case 'watchlist_remove':
      return dispatch(removeFromWatchlist(action.symbol)).unwrap();
    case 'analysis_exclude':
    case 'analysis_include':
      return dispatch(setAnalysisExclusion({ symbol: action.symbol, excluded: action.type === 'analysis_exclude' })).unwrap();
    case 'debate_start':
      return startDebate({ symbol: action.symbol, description: action.description, question: action.question })
        .then((runId) => ({ navigateTo: debateLivePath(runId) }));
    case 'order_ticket':
      return Promise.resolve({ navigateTo: orderTicketPath(action) });
    case 'trading_pause':
      return tradingApi.pause(action.reason || 'demandé à Gladys', false);
    case 'order_modify':
      return eachOrder(action, (t) => tradingApi.modify(t.id, { ...action.change, confirm_live: action.env === 'live' && confirmLive }));
    case 'order_cancel':
      return eachOrder(action, (t) => tradingApi.cancel(t.id));
    default:
      return Promise.reject(new Error('Action inconnue.'));
  }
};

// Returns { ok, error, navigateTo } (navigateTo: the live debate just
// started, or the pre-filled order ticket).
export const runChatAction = async (dispatch, messageId, action, options) => {
  let result;
  try {
    result = await execute(dispatch, action, options);
  } catch (err) {
    await dispatch(settleChatAction({ messageId, actionId: action.id, status: 'failed' }));
    return { ok: false, error: errorText(err) };
  }
  await dispatch(settleChatAction({ messageId, actionId: action.id, status: 'done' }));
  return { ok: true, navigateTo: result?.navigateTo };
};

export const dismissChatAction = (dispatch, messageId, action) => dispatch(settleChatAction({ messageId, actionId: action.id, status: 'dismissed' }));
